/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentProps, JSX } from 'preact';
import { classNames } from '#utils/UNSAFE_classNames';
import { useUser } from '#hooks/UNSAFE_useUser';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import { BarTrack, BarValue } from '#PRIVATE_Meter';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { ProgressBarStyles, ProgressBarVariantOptions } from './themes/ProgressBarStyles.css';
import { ProgressBarRedwoodTheme } from './themes/redwood/ProgressBarTheme';

type ariaPropsType = ComponentProps<typeof BarTrack>['ariaProps'];

type IntrinsicProps = Pick<UnpackSignals<JSX.IntrinsicElements['div']>, 'class'>;

type ProgressBarProps = IntrinsicProps & {
  /**
   * Specifies the id of the progress bar.  If the progress bar is being used to describe the loading process of a particular region
   * of the page, then the aria-describedby attribute of the region must point to the id of the ProgressBar
   */
  id?: UnpackSignals<JSX.IntrinsicElements['div']>['id'];
  /**
   * The value of the progress bar.  If the value is 'indeterminate', an indeterminate progress bar will be shown.
   */
  value?: 'indeterminate' | number;

  /**
   * Whether the progress bar is positioned at the top edge of a container or not.
   * If set to 'top', the curved borders will be removed.
   */
  edge?: 'none' | 'top';

  /**
   * The maximum value of the progress bar.
   */
  max?: number;
  /**
   * The width of the progress bar.  This will use the CSS lengths styling.
   */
  // TODO: use the dimensions work the Jeanne is doing JET-49961
  width?: string;

  /**
   * A label to be used for accessibility purposes when value is set to 'indeterminate'. This will be used for the aria-valuetext of the DOM element.
   */
  'aria-valuetext'?: string;
  /**
   * A label to be used for accessibility purposes. This will be used for the aria-label of the DOM element.
   */
  'aria-label'?: string;

  /**
   * One or more ids (separated by spaces) of elements that label the progress bar. This will be used for the aria-labelledby of the DOM element.
   */
  'aria-labelledby'?: string;
};
/**
 * A progress bar allows the user to visualize the progression of an extended computer operation.
 */
export function ProgressBar({ value, max, ...otherProps }: ProgressBarProps) {
  return value === 'indeterminate' ? (
    <IndeterminateProgressBar {...otherProps} />
  ) : (
    <DeterminateProgressBar value={value} max={max} {...otherProps} />
  );
}

type IndeterminateProps = Omit<ProgressBarProps, 'value' | 'max'>;

const IndeterminateProgressBar = ({ id, width, edge = 'none', ...props }: IndeterminateProps) => {
  const {
    classes,
    styles: {
      valueStyle,
      indeterminate,
      animationStyleLTR,
      animationStyleRTL,
      highContrastValueStyle,
      trackBorderEdgeNone,
      trackBorderEdgeTop
    }
  } = useComponentTheme<ProgressBarVariantOptions, ProgressBarStyles>(ProgressBarRedwoodTheme, {
    edge: edge === 'none' ? edge : undefined
  });

  const { direction, forcedColors } = useUser();
  const barValueClasses = classNames([
    valueStyle,
    indeterminate,
    direction === 'rtl' ? animationStyleRTL : animationStyleLTR,
    forcedColors === 'active' ? highContrastValueStyle : ''
  ]);
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const ariaProps: ariaPropsType = {
    'aria-valuetext': props['aria-valuetext'] || translations.progressIndeterminate(),
    'aria-label': props['aria-label'],
    'aria-labelledby': props['aria-labelledby'],
    role: 'progressbar'
  };
  const trackBorderClass =
    forcedColors === 'active'
      ? undefined
      : edge === 'none'
      ? trackBorderEdgeNone
      : trackBorderEdgeTop;
  return (
    <BarTrack
      id={id}
      ariaProps={ariaProps}
      class={classNames([classes, trackBorderClass])}
      length={width}>
      <BarValue class={barValueClasses} />
    </BarTrack>
  );
};

type DeterminateProps = Omit<ProgressBarProps, 'aria-valuetext'> & {
  value?: number;
};

const DeterminateProgressBar = ({
  value = 0,
  max = 100,
  id,
  width,
  edge = 'none',
  ...props
}: DeterminateProps) => {
  const { forcedColors } = useUser();
  const {
    classes,
    styles: { valueStyle, highContrastValueStyle, trackBorderEdgeNone, trackBorderEdgeTop }
  } = useComponentTheme<ProgressBarVariantOptions, ProgressBarStyles>(ProgressBarRedwoodTheme, {
    edge: edge === 'none' ? edge : undefined
  });

  const barValueClasses = classNames([
    valueStyle,
    forcedColors === 'active' ? highContrastValueStyle : ''
  ]);

  const percentage = `${Math.min(Math.max(0, value / max), 1) * 100}%`;
  const ariaProps: ariaPropsType = {};
  ariaProps['aria-valuemin'] = value != null ? 0 : undefined;
  ariaProps['aria-valuemax'] = value != null ? max : undefined;
  ariaProps['aria-valuenow'] = value != null ? value : undefined;
  ariaProps['role'] = 'progressbar';
  (ariaProps['aria-label'] = props['aria-label']),
    (ariaProps['aria-labelledby'] = props['aria-labelledby']);
  return (
    <BarTrack
      ariaProps={ariaProps}
      id={id}
      length={width}
      class={classNames([classes, edge === 'none' ? trackBorderEdgeNone : trackBorderEdgeTop])}>
      <BarValue class={barValueClasses} length={percentage} />
    </BarTrack>
  );
};
